import { CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import { BarChart, HighlightItemData } from "@mui/x-charts";
import { useEffect, useMemo, useState, useRef } from "react";
import { ActionItemGroupByKey } from "../../utils/utils";
import { ChartDataItem } from "../../utils/types";

// Adjust the type of ChartDataItem to include an index signature


interface CommonBarChartProps {
  data: ChartDataItem[];
  height?: number;
  barWidthPx?: number;
  gapPx?: number;
  minWidth?: number;
  hoverColor?: string;
  fadedColor?: string;
  loading?: boolean;
  onBarClick?: (item: ChartDataItem) => void;
  selectedBarKey?: string | undefined
  enableOthersDots?: boolean;
  groupBy?: ActionItemGroupByKey;
  hoverText?: string;
  onOthersDotsClick?: (anchorEl: HTMLElement, item: ChartDataItem) => void;
  dotsHighlighted?: boolean;
}

const CommonBarChart = ({
  data,
  height = 230,
  barWidthPx = 32,
  gapPx = 82,
  minWidth = 900,
  hoverColor = "#5731C3",
  fadedColor = "#CEC1F2",
  loading = false,
  onBarClick,
  selectedBarKey,
  enableOthersDots,
  hoverText,
  onOthersDotsClick,
  dotsHighlighted = false,
}: CommonBarChartProps) => {
  const [highlightedItem, setHighlightedItem] = useState<HighlightItemData | null>(null);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(minWidth);
  const isDotsMode = enableOthersDots && data.some(d => d.isDots);

  const barCount = data.length;

  // Margin values for the chart
  const leftMargin = 45;
  const rightMargin = 12;
  const topMargin = 40;
  const bottomMargin = 14; // Increased to prevent scrollbar from covering labels

  const maxValue = useMemo(() => {
    if (!data.length) return 5;

    // Exclude "dots" placeholders from scaling so the Y-axis reflects visible bars
    const max = Math.max(...data.map((item) => (item.isDots ? 0 : item.value)));

    // CRITICAL: chart cannot render when min === max
    if (max === 0) return 5;
  
    return Math.ceil(max / 5) * 5;
  }, [data]);
  
  // zero bar should look like a thin line (~1.5% of axis height)
  const ZERO_BAR_RATIO = 0.008;

  // hard clamp so zero NEVER looks like data
  const MAX_ZERO_RATIO = 0.01;

  const zeroDisplayValue = Math.min(
    maxValue * ZERO_BAR_RATIO,
    maxValue * MAX_ZERO_RATIO
  );

  const chartData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      displayValue: item.isDots ? 0 : item.value === 0 ? zeroDisplayValue : item.value,
    }));
  }, [data]);
  

  const selectedIndex = useMemo(() => {
    if (!selectedBarKey) return null;
    return data.findIndex((d) => d.key === selectedBarKey);
  }, [data, selectedBarKey]);
  
  useEffect(() => {
    if (selectedIndex === null) {
      setHighlightedItem(null);
    } else {
      setHighlightedItem({ seriesId: "count", dataIndex: selectedIndex });
    }
  }, [selectedIndex]);

  // Monitor container width changes
  useEffect(() => {
    const updateWidth = () => {
      if (scrollableContainerRef.current) {
        const width = scrollableContainerRef.current.offsetWidth;
        setContainerWidth(width);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const chartContentWidth = useMemo(() => {
    if (isDotsMode) return containerWidth;
    if (!barCount) return containerWidth;

    // Calculate the width needed for all bars with desired gaps
    const naturalWidth = barCount * (barWidthPx + gapPx) + rightMargin;

    // If natural width fits in container, use it; otherwise use container width
    // This prevents scrollbar when bars can fit with reduced gaps
    if (naturalWidth <= containerWidth) {
      return containerWidth;
    }
    if (barCount <= 10) {
      return containerWidth;
    }
    // If natural width exceeds minWidth, allow scrolling
    return Math.max(naturalWidth, minWidth);
  }, [barCount, barWidthPx, gapPx, rightMargin, minWidth, containerWidth]);
  const categoryGapRatio = useMemo(() => {
    if (!barCount) return 0;
    
    // Calculate the available space for bars (excluding margins)
    const availableWidth = chartContentWidth - rightMargin;
    
    // Calculate the total bandwidth needed per bar
    const bandWidth = availableWidth / barCount;
    
    // Calculate gap as the remaining space after the fixed bar width
    const actualGap = bandWidth - barWidthPx;
    
    // Return the ratio of gap to total bandwidth
    return Math.max(0, actualGap / bandWidth);
  }, [barCount, chartContentWidth, barWidthPx, rightMargin]);

  const truncateLabel = (text: string, maxChars = 10) => {
    if (!text) return "";
    return text.length > maxChars ? `${text.slice(0, maxChars)}…` : text;
  };

  // Dynamically compute how many characters fit in each bar's band without
  // No upper cap — when there is enough space the full label is shown.
  const dynamicMaxChars = useMemo(() => {
    if (!barCount || !chartContentWidth) return 10;
    const bandWidth = chartContentWidth / barCount; // px per bar slot
    const chars = Math.floor(bandWidth / 6.5);
    return Math.max(4, chars); // minimum 4 chars, no upper limit
  }, [barCount, chartContentWidth]);


  const Dot = ({ active }: { active?: boolean }) => (
    <Box
      sx={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        backgroundColor: active ? hoverColor : "#B6A3EC",
      }}
    />
  );
  return (
    <Box 
      width="100%" 
      height={height} 
      position={"relative"}
      display="flex"
      sx={{
        maxWidth: "100%",
        boxSizing: "border-box",
        marginLeft: "-10px" 
      }}
    >
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.6)"
          zIndex={2}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      
      {/* Fixed Y-Axis */}
      <Box
        width={leftMargin}
        flexShrink={0}
        height={height}
        position="relative"
      >
        <BarChart
          width={leftMargin}
          height={height}
          dataset={chartData as Array<{ label: string; value: number }>}
          loading={false}
          borderRadius={3}
          axisHighlight={{ x: "none", y: "none" }}
          yAxis={[
            {
              min: 0,
              max: maxValue,
              tickSize: 0,
              tickLabelStyle: {
                fill: "#6B7280",
                fontSize: 12,
                textAnchor: "end",
              },
            },
          ]}
          margin={{
            top: topMargin,
            bottom: bottomMargin,
            right: 0,
            left: 0,
          }}
          xAxis={[
            {
              scaleType: "band",
              dataKey: "label",
              tickSize: 0,
              tickLabelStyle: { display: "none" },
              valueFormatter: (value) => {
                const item = data.find((d) => d.label === value);
              
                if (item?.isDots) {
                  return "";
                }
              
                return truncateLabel(value, dynamicMaxChars);
              },
            },
          ]}
          series={[
            {
              id: "count",
              dataKey: "displayValue",
              label: "",
              color: "transparent",
            },
          ]}
          grid={{ horizontal: true, vertical: false }}
          slotProps={{
            legend: { sx: { display: "none" } },
          }}
          sx={{
            "& .MuiChartsAxis-left .MuiChartsAxis-line": {
              display: "none",
            },
            "& .MuiChartsAxis-bottom": {
              display: "none",
            },
            "& .MuiBarElement-root": {
              display: "none",
              maxWidth: `${barWidthPx}px`,
              width: `${barWidthPx}px`,
            },
            "& .MuiChartsGrid-line": {
              stroke: "#DDDDDD",
              strokeWidth: 0.5,
            },
            "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": {
              textAnchor: "end",
              transform: "translateX(-8px)",
            },
          }}
        />
      </Box>

      {/* Scrollable Bars Area */}
      <Box
        ref={scrollableContainerRef}
        flex={1}
        height={height}
        sx={{
          // Only show scrollbar when content genuinely overflows.
          // Using "hidden" when content fits prevents the flash of a
          // horizontal scrollbar during loading / page transitions.
          overflowX: (isDotsMode || chartContentWidth <= containerWidth) ? "hidden" : "auto",
          overflowY: "hidden",
          minWidth: 0, // Allow flex item to shrink
          width: 0, // Force flex to respect container boundaries
          flexBasis: 0, // Start from 0 and grow
          "&::-webkit-scrollbar": {
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        <Box width={chartContentWidth}>
          <BarChart
            width={chartContentWidth}
            height={height}
            dataset={chartData as Array<{ label: string; value: number }>}
            loading={false}
            borderRadius={3}
            axisHighlight={{ x: "none", y: "none" }}
            onItemClick={(_, params) => {
              const { dataIndex } = params;
            
              const clickedItem = data[dataIndex];
            
              if (clickedItem) {
                onBarClick?.(clickedItem);
              }
            }}
            highlightedItem={highlightedItem}
            onHighlightChange={(item) => {
              if (!item && selectedIndex !== null) {
                setHighlightedItem({
                  seriesId: "count",
                  dataIndex: selectedIndex,
                });
              } else {
                setHighlightedItem(item);
              }
            }}
            yAxis={[
              {
                min: 0,
                max: maxValue,
                tickSize: 0,
                tickLabelStyle: {
                  display: "none", // Hide y-axis labels in scrollable area
                },
              },
            ]}
            margin={{
              top: topMargin,
              bottom: bottomMargin,
              right: rightMargin,
              left: -20, // No left margin since y-axis is separate
            }}
        xAxis={[
          {
            scaleType: "band",
            dataKey: "label",
            categoryGapRatio,
            tickPlacement: "middle",
            tickLabelPlacement: "middle",
            valueFormatter: (value) => {
              const item = data.find((d) => d.label === value);
              if (item?.isDots) {
                return "";
              }
              return truncateLabel(value, dynamicMaxChars);
            },
            barGapRatio: 0,
            tickSize: 0,
            tickLabelStyle: {
              fontSize: 10,
              fill: "#000",
              whiteSpace: "pre-line",
              lineHeight: 1.2,
              textAnchor: "middle",
            },
          },
        ]}
          series={[
            {
              id: "count",
              dataKey: "displayValue",
              label: "",
              color: fadedColor,
              highlightScope: {
                highlight: "item",
                fade: "global",
              },
              valueFormatter: (_, context) => {
                const row = data[context.dataIndex] as ChartDataItem;
                return `${row.fullLabel ?? row.label}\n${row.status} ${hoverText}: ${row.value}`;
              },
            },
          ]}
        grid={{ horizontal: true, vertical: false }}
        sx={{
          "& .MuiChartsAxis-left .MuiChartsAxis-line": {
            display: "none",
          },
          "& .MuiBarElement-faded": {
            fill: fadedColor,
          },
          "& .MuiBarElement-highlighted": {
            fill: hoverColor,
          },
          "& .MuiBarElement-root:hover": {
            opacity: 1,
            filter: "none",
          },
          "& .MuiBarElement-root.Mui-active": {
            opacity: 1,
            filter: "none",
          },
          "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
            stroke: "#c0c0c0",   // 👈 your desired X-axis line color
            strokeWidth: 1,
          },
          "& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel": {
            transform: "translateY(8px)",
          },
          "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": {
            transform: "translateX(-8px)",
          },
          "& .MuiChartsGrid-line": {
            stroke: "#DDDDDD",
            strokeWidth: 0.5,
          },
          "& .MuiBarElement-root[data-value='0']": {
            opacity: 0.6,
            strokeDasharray: "2 2",
          },
          "& .MuiBarElement-root": {
            maxWidth: `${barWidthPx}px`,
            width: `${barWidthPx}px`,
          },
        }}
        slotProps={{
          legend: { sx: { display: "none" } },
          tooltip: { trigger: "item",
            sx: {
              whiteSpace: "pre-line",
              "& .MuiChartsTooltip-mark": {
                display: "none",
              },
              "& .MuiChartsTooltip-labelCell": {
                display: "none",
              },
              "& .MuiChartsTooltip-valueCell": {
                fontSize: 12,
                fontWeight: 400,
                color: "#000",          // 👈 darker color for second line
              },
        
              /* FIRST LINE override */
              "& .MuiChartsTooltip-valueCell::first-line": {
                color: "#6F6F7F",       // 👈 lighter color for label
                fontWeight: 500,
              },
              "& .MuiChartsTooltip-valueCell span:last-child": {
                whiteSpace: "nowrap",
              },
            },
          },
        }}
          />
          {isDotsMode &&
            data.map((item, index) => {
              if (!item.isDots) return null;

              const slotWidth = chartContentWidth / data.length;
              const left = slotWidth * index + slotWidth / 1.1 - 10;
              const isSelected = selectedBarKey === item.key;

              return (
                <Box
                  sx={{
                    position: "absolute",
                    left,
                    bottom: 18,
                    transform: "translateX(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    // Hovering anywhere (dots OR label) enlarges the dots
                    "& .dots-row > *": {
                      transition: "width 0.15s ease, height 0.15s ease, background-color 0.15s ease",
                    },
                    "&:hover .dots-row > *, & .dots-row.dots-active > *": {
                      width: "7px !important",
                      height: "7px !important",
                      backgroundColor: `${hoverColor} !important`,
                    },
                  }}
                  onClick={(event) => {
                    if (onOthersDotsClick) {
                      onOthersDotsClick(event.currentTarget as HTMLElement, item);
                      return;
                    }

                    onBarClick?.(item);
                  }}
                >
                  <Box
                    className={`dots-row${dotsHighlighted ? " dots-active" : ""}`}
                    sx={{
                      display: "flex",
                      gap: "4px",
                    }}
                  >
                    <Dot active={isSelected} />
                    <Dot active={isSelected} />
                    <Dot active={isSelected} />
                  </Box>

                  <Box
                    sx={{
                      marginTop: "26px",
                      fontSize: "10px",
                      color: "#000",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    }}
                  >
                    Other Accounts
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>
    </Box>
  );
};

export default CommonBarChart;
